export const OPERATOR_VISIBLE_SETTLEMENT_STATUSES = [
  "DRAFT",
  "AWAITING_APPROVAL",
  "RETURNED",
  "APPROVED",
  "REJECTED",
  "VOIDED",
  "REVERSED",
] as const;

export const REPLACEMENT_FEE_POLICY =
  "Replacement Settlements recalculate from currently valid source recovery data using the governed fee rule applicable under the existing payment-date / effective-date model. Original fees, excluded lines, and settlement-level adjustments are not copied. Historical rule/version evidence remains on the original Settlement.";

export const SETTLEMENT_REVERSAL_BANNER = "This Settlement has been reversed.";

export type SettlementLineageRecord = {
  settlementId: string;
  settlementNumber?: string | null;
  settlementStatus?: string | null;
  reversedAt?: string | null;
  reversedByUserId?: string | null;
  reversedByName?: string | null;
  reversalReason?: string | null;
  reversedFromSettlementId?: string | null;
  reversedFromSettlementNumber?: string | null;
  replacementSettlementId?: string | null;
  replacementSettlementNumber?: string | null;
};

export type SettlementReversalBanner = {
  settlementId: string;
  reversedAt: string;
  reversedByName: string;
  reversalReason: string;
  replacementSettlementId: string | null;
  replacementSettlementNumber: string | null;
  replacementStatus: "created" | "not_created";
};

export type SettlementLineageView = {
  settlementId: string;
  originalLabel: string | null;
  originalSettlementId: string | null;
  originalSettlementNumber: string | null;
  replacementLabel: string | null;
  replacementSettlementId: string | null;
  replacementSettlementNumber: string | null;
};

export function settlementReversalBanner(settlement: SettlementLineageRecord | null | undefined): SettlementReversalBanner | null {
  if (!settlement?.settlementId) return null;
  if (String(settlement.settlementStatus || "").toUpperCase() !== "REVERSED") return null;
  return {
    settlementId: settlement.settlementId,
    reversedAt: String(settlement.reversedAt || ""),
    reversedByName: String(settlement.reversedByName || "unknown"),
    reversalReason: String(settlement.reversalReason || ""),
    replacementSettlementId: settlement.replacementSettlementId || null,
    replacementSettlementNumber: settlement.replacementSettlementNumber || null,
    replacementStatus: settlement.replacementSettlementId ? "created" : "not_created",
  };
}

export function settlementLineage(settlement: SettlementLineageRecord | null | undefined): SettlementLineageView | null {
  if (!settlement?.settlementId) return null;
  const originalNumber = settlement.reversedFromSettlementNumber || settlement.reversedFromSettlementId;
  const replacementNumber = settlement.replacementSettlementNumber || settlement.replacementSettlementId;
  if (!originalNumber && !replacementNumber) return null;
  return {
    settlementId: settlement.settlementId,
    originalLabel: originalNumber ? `Replacement for ${originalNumber}` : null,
    originalSettlementId: settlement.reversedFromSettlementId || null,
    originalSettlementNumber: settlement.reversedFromSettlementNumber || null,
    replacementLabel: replacementNumber ? `Replaced by ${replacementNumber}` : null,
    replacementSettlementId: settlement.replacementSettlementId || null,
    replacementSettlementNumber: settlement.replacementSettlementNumber || null,
  };
}

export function isOperatorVisibleSettlementStatus(status: string) {
  return OPERATOR_VISIBLE_SETTLEMENT_STATUSES.includes(String(status || "").toUpperCase() as (typeof OPERATOR_VISIBLE_SETTLEMENT_STATUSES)[number]);
}

export function statementBelongsToSettlement(
  statement: { settlementId?: string | null } | null | undefined,
  settlementId: string,
) {
  if (!statement) return false;
  if (!statement.settlementId) return true;
  return statement.settlementId === settlementId;
}
